import { useState } from 'react';
import { FlexCenter, FlexColumn } from '@elementary/common-ui/components';
import EmptyStateBox from '@elementary/common-ui/components/EmptyStateBox';
import { dateRangeFromDaysBack } from '@elementary/common-ui/components/DateRangePicker';
import useSidePanelParams from '@elementary/common-ui/hooks/useSidePanelParams';
import { isElementaryCloudTest } from '@elementary/common-ui/utils/common';
import { safeParsePositiveInt } from '@elementary/common-ui/utils/safeTypes';
import { CircularProgress } from '@mui/material';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import { AssetDetails } from 'shared/components/Asset';
import { ErrorBoundary } from 'react-error-boundary';
import { DAYS_BACK } from '@elementary/common-ui/constants/urlParams';
import { useDefaultDateRange } from 'shared/components/ExecutionHistory';
import PanelError from 'shared/components/PanelError';
import useTestOverviewData from '../hooks/useTestOverviewData';
import usePanelTracking from '../hooks/usePanelTracking';
import { TestOverview as TestOverviewType } from '../types';
import FeedbackDialog from './FeedbackDialog';
import PanelHeader from './PanelHeader';
import TestOverview from './TestOverview';

type Props = {
  hideCloseButton?: boolean;
  hideHeader?: boolean;
};

const Panel = ({ hideCloseButton, hideHeader }: Props) => {
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false);
  const { sourceType, selectedId, selectedDrawerType } = useSidePanelParams();
  const { data, allExecutions, isLoading, error } = useTestOverviewData();
  const [searchParams] = useSearchParams();
  const defaultDaysBack = useDefaultDateRange();

  usePanelTracking({ data });

  const isCloudTest =
    sourceType === 'test' &&
    isElementaryCloudTest((data as TestOverviewType)?.test.type);

  const daysBackParam = searchParams.get(DAYS_BACK);
  const daysBack = safeParsePositiveInt(daysBackParam, defaultDaysBack);
  const dateRange = dateRangeFromDaysBack(daysBack);

  return (
    <ErrorBoundary
      resetKeys={[selectedId, selectedDrawerType]}
      fallbackRender={({ error }) => (
        <PanelError error={error} page={'Panel'} />
      )}
    >
      {selectedDrawerType === 'test' || selectedDrawerType === 'cloud_test' ? (
        <>
          {!hideHeader && <PanelHeader hideCloseButton={hideCloseButton} />}

          {isLoading || !data || error ? (
            <FlexCenter sx={{ height: '100%', padding: '0 24px' }}>
              {error ? (
                <PanelError error={error as Error} errorName="ApiError" />
              ) : !isLoading && !data ? (
                <EmptyStateBox title="No data available">
                  No execution data found for this test
                </EmptyStateBox>
              ) : (
                <CircularProgress />
              )}
            </FlexCenter>
          ) : (
            <FlexColumn sx={{ height: '100%', gap: 2 }}>
              {sourceType === 'test' && selectedId && (
                <TestOverview
                  testOverview={data as TestOverviewType}
                  allExecutions={allExecutions}
                  testId={selectedId}
                  onFeedbackClick={
                    isCloudTest
                      ? () => setIsFeedbackDialogOpen(true)
                      : undefined
                  }
                  dateRange={dateRange}
                />
              )}
            </FlexColumn>
          )}
          {sourceType === 'test' && (
            <FeedbackDialog
              open={isFeedbackDialogOpen}
              onClose={() => setIsFeedbackDialogOpen(false)}
              testMetadata={{
                testUniqueId: (data as TestOverviewType)?.test.id,
                latestRunTime: (data as TestOverviewType)?.execution
                  ?.executionTimestamp,
                latestRunStatus: (data as TestOverviewType)?.execution?.status,
              }}
            />
          )}
        </>
      ) : (
        <FlexColumn sx={{ height: '100%' }}>
          <AssetDetails id={selectedId!} isTest={sourceType === 'test'} />
        </FlexColumn>
      )}
    </ErrorBoundary>
  );
};

export default Panel;
